import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Flame, Zap, Users, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useShortageSignalForOccupation } from '@/hooks/useShortageSignals';

interface DemandSignalBlockProps {
  socCode: string | undefined;
  occupationTitle: string;
}

const DemandSignalBlock = ({ socCode, occupationTitle }: DemandSignalBlockProps) => {
  const { data: signal, isLoading } = useShortageSignalForOccupation(socCode);

  if (isLoading || !signal || signal.shortage_level === 'normal') return null;

  const levelConfig = {
    'critical-shortage': {
      gradient: 'from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20',
      border: 'border-red-200 dark:border-red-800',
      icon: Flame,
      iconColor: 'text-red-600 dark:text-red-400',
      title: 'Critical Worker Shortage',
      description: `${occupationTitle} is experiencing a critical labor shortage. Employers are actively competing for qualified candidates.`,
    },
    'high-demand': {
      gradient: 'from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20',
      border: 'border-orange-200 dark:border-orange-800',
      icon: Zap,
      iconColor: 'text-orange-600 dark:text-orange-400',
      title: 'High Demand Occupation',
      description: `${occupationTitle} is in high demand with strong job growth projected over the next decade.`,
    },
    'moderate-demand': {
      gradient: 'from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20',
      border: 'border-amber-200 dark:border-amber-800',
      icon: TrendingUp,
      iconColor: 'text-amber-600 dark:text-amber-400',
      title: 'Growing Demand',
      description: `${occupationTitle} shows strong employment growth with significant annual job openings.`,
    },
  }[signal.shortage_level];

  if (!levelConfig) return null;

  const Icon = levelConfig.icon;

  const formatNumber = (num: number) =>
    new Intl.NumberFormat('en-US').format(Math.round(num));

  return (
    <Card className={`bg-gradient-to-r ${levelConfig.gradient} ${levelConfig.border}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className={`h-5 w-5 ${levelConfig.iconColor}`} />
          {levelConfig.title}
          {signal.bright_outlook && (
            <Badge variant="secondary" className="text-xs ml-2">
              Bright Outlook
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          {levelConfig.description}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-bold">
              {signal.pct_change > 0 ? '+' : ''}{signal.pct_change}%
            </div>
            <div className="text-xs text-muted-foreground">10-Year Growth</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-bold">
              {formatNumber(signal.annual_openings)}
            </div>
            <div className="text-xs text-muted-foreground">Annual Openings</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-bold">
              {formatNumber(signal.proj_employment)}
            </div>
            <div className="text-xs text-muted-foreground">Projected Jobs (2033)</div>
          </div>

          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Zap className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-bold capitalize">
              {signal.outlook_band.replace('-', ' ')}
            </div>
            <div className="text-xs text-muted-foreground">Outlook</div>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-border/50">
          <Link
            to="/in-demand-careers/"
            className="text-sm text-primary hover:underline font-medium"
          >
            View all in-demand careers →
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default DemandSignalBlock;
